import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { WhitelabelProvider } from "@/contexts/WhitelabelContext";
import { logRequest, setupGlobalErrorHandler } from "@/hooks/useRequestLogger";
import { Loader2 } from "lucide-react";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { GlobalHeadManager } from "./components/GlobalHeadManager";
// All routes lazy loaded for better initial performance
const ModularLanding = lazy(() => import("./pages/ModularLanding"));
const NicheLanding = lazy(() => import("./pages/NicheLanding"));
const Auth = lazy(() => import("./pages/Auth"));

// Lazy loaded routes for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const VerifyEmailPending = lazy(() => import("./pages/VerifyEmailPending"));
const Booking = lazy(() => import("./pages/Booking"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const PublicQuote = lazy(() => import("./pages/PublicQuote"));
const PublicContract = lazy(() => import("./pages/PublicContract"));
const PublicSignature = lazy(() => import("./pages/PublicSignature"));
const SignatureReceipt = lazy(() => import("./pages/SignatureReceipt"));
const PublicForm = lazy(() => import("./pages/PublicForm"));
const PublicLinktree = lazy(() => import("./pages/PublicLinktree"));
const PublicCatalog = lazy(() => import("./pages/PublicCatalog"));
const PublicMiniSite = lazy(() => import("./pages/PublicMiniSite"));
const PublicSurvey = lazy(() => import("./pages/PublicSurvey"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const QueueTotem = lazy(() => import("./pages/QueueTotem"));
const QueueDisplay = lazy(() => import("./pages/QueueDisplay"));
const QueueOffice = lazy(() => import("./pages/QueueOffice"));
const PublicWhatsappGroup = lazy(() => import("./pages/PublicWhatsappGroup"));
const PublicTimeClock = lazy(() => import("./pages/PublicTimeClock"));
const WristbandSecurityCheck = lazy(() => import("./pages/WristbandSecurityCheck"));
const WaiterWristbands = lazy(() => import("./pages/WaiterWristbands"));
const WaiterTables = lazy(() => import("./pages/WaiterTables"));
const KitchenDisplay = lazy(() => import("./pages/KitchenDisplay"));
const TableOrder = lazy(() => import("./pages/TableOrder"));
const PublicResourceBooking = lazy(() => import("./pages/PublicResourceBooking"));
const PublicStore = lazy(() => import("./pages/PublicStore"));
const PublicProductDetail = lazy(() => import("./pages/PublicProductDetail"));
const PublicReview = lazy(() => import("./pages/PublicReview"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Institucional pages
const About = lazy(() => import("./pages/institucional/About"));
const Contact = lazy(() => import("./pages/institucional/Contact"));
const Careers = lazy(() => import("./pages/institucional/Careers"));
const Partners = lazy(() => import("./pages/institucional/Partners"));
const Features = lazy(() => import("./pages/institucional/Features"));
const Pricing = lazy(() => import("./pages/institucional/Pricing"));
// Integrations page removed
const Updates = lazy(() => import("./pages/institucional/Updates"));
const Privacy = lazy(() => import("./pages/institucional/Privacy"));
const Terms = lazy(() => import("./pages/institucional/Terms"));
const LGPD = lazy(() => import("./pages/institucional/LGPD"));
const Cookies = lazy(() => import("./pages/institucional/Cookies"));
const Whitelabel = lazy(() => import("./pages/institucional/Whitelabel"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

// Route change logger component
const RouteLogger = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Log page view
    logRequest('VIEW', location.pathname, 200, 0, user?.id);
  }, [location.pathname, user?.id]);

  useEffect(() => {
    // Setup global error handler
    setupGlobalErrorHandler(user?.id);
  }, [user?.id]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <WhitelabelProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <GlobalHeadManager />
            <RouteLogger />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<ModularLanding />} />
              {/* Niche Landing Pages */}
              <Route path="/lp/:niche" element={<NicheLanding />} />
              {/* Legacy routes for backwards compatibility */}
              <Route path="/moda" element={<NicheLanding />} />
              <Route path="/beleza" element={<NicheLanding />} />
              <Route path="/servicos" element={<NicheLanding />} />
              <Route path="/marketing" element={<NicheLanding />} />
              <Route path="/academia" element={<NicheLanding />} />
              <Route path="/imobiliaria" element={<NicheLanding />} />
              <Route path="/clinica" element={<NicheLanding />} />
              <Route path="/contabilidade" element={<NicheLanding />} />
              <Route path="/alimentacao" element={<NicheLanding />} />
              {/* Institucional pages */}
              <Route path="/sobre" element={<About />} />
              <Route path="/contato" element={<Contact />} />
              <Route path="/carreiras" element={<Careers />} />
              <Route path="/parceiros" element={<Partners />} />
              <Route path="/funcionalidades" element={<Features />} />
              <Route path="/precos" element={<Pricing />} />
              {/* Integrations route removed */}
              <Route path="/atualizacoes" element={<Updates />} />
              <Route path="/privacidade" element={<Privacy />} />
<Route path="/termos" element={<Terms />} />
<Route path="/lgpd" element={<LGPD />} />
<Route path="/cookies" element={<Cookies />} />
<Route path="/whitelabel" element={<Whitelabel />} />
              {/* Auth routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/accept-invite" element={<AcceptInvite />} />
              <Route path="/auth/verify-email/pending" element={<VerifyEmailPending />} />
              <Route path="/agendar/:slug" element={<Booking />} />
              <Route path="/orcamento/:token" element={<PublicQuote />} />
              <Route path="/contrato/:token" element={<PublicContract />} />
              <Route path="/assinar/:token" element={<PublicSignature />} />
              <Route path="/comprovante/:signatureId" element={<SignatureReceipt />} />
              <Route path="/formulario/:formId" element={<PublicForm />} />
              {/* Public routes */}
              <Route path="/u/:username" element={<PublicLinktree />} />
              <Route path="/catalogo/:slug" element={<PublicCatalog />} />
              <Route path="/catalog/:slug" element={<PublicCatalog />} />
              <Route path="/loja/:slug" element={<PublicStore />} />
              <Route path="/loja/:slug/produto/:productId" element={<PublicProductDetail />} />
              <Route path="/store/:slug" element={<PublicStore />} />
              <Route path="/store/:slug/product/:productId" element={<PublicProductDetail />} />
              <Route path="/site/:slug" element={<PublicMiniSite />} />
              <Route path="/pesquisa/:token" element={<PublicSurvey />} />
              <Route path="/c/:token" element={<CustomerPortal />} />
              {/* Queue System */}
              <Route path="/fila/:slug/totem" element={<QueueTotem />} />
              <Route path="/fila/:slug/display" element={<QueueDisplay />} />
              <Route path="/fila/:slug/atendimento" element={<QueueOffice />} />
              {/* Legacy route for backwards compatibility */}
              <Route path="/fila/:slug/consultorio" element={<QueueOffice />} />
              {/* WhatsApp Groups */}
              <Route path="/grupo/:slug" element={<PublicWhatsappGroup />} />
              <Route path="/grupo/:slug/:groupId" element={<PublicWhatsappGroup />} />
              {/* Public Time Clock */}
              <Route path="/ponto/:slug" element={<PublicTimeClock />} />
              {/* Wristband Security Check - by slug */}
              <Route path="/seguranca/:slug" element={<WristbandSecurityCheck />} />
              {/* Waiter Pages - by slug */}
              <Route path="/garcom/:slug/pulseiras" element={<WaiterWristbands />} />
              <Route path="/garcom/:slug/mesas" element={<WaiterTables />} />
              {/* Kitchen Display */}
              <Route path="/cozinha/:slug" element={<KitchenDisplay />} />
              {/* Table Order via QR Code */}
              <Route path="/mesa/:qrToken" element={<TableOrder />} />
              {/* Public Resource Booking */}
              <Route path="/reservar/:slug" element={<PublicResourceBooking />} />
              {/* Public Review Page */}
              <Route path="/avaliar/:slug" element={<PublicReview />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notificacoes"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </BrowserRouter>
        </TooltipProvider>
      </WhitelabelProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
